import type { Row } from "@tanstack/react-table"
import { useEffect } from "react"

import usePurchaseOrderForm from "../../hooks/usePurchaseOrderForm"
import { getUpdatedLines } from "../utils"

import type { OrderLine, PurchaseOrderPurchasingCost } from "@/graphql/codegen/graphql"
import { useCalculatedPricing } from "@/screens/Procurement/PurchaseOrder/contexts/PurchaseOrderContext"
import type { UomAmountWithId } from "@/types/convertion"

interface UsePriceUomColumn {
  price: UomAmountWithId | null
  convertedPrice: UomAmountWithId | null
  total: number
  fetching: boolean
  isManualPriceSet: boolean
  pricingPackage?: PurchaseOrderPurchasingCost
}

export const usePriceUomColumn = ({ row }: { row: Row<OrderLine> }): UsePriceUomColumn => {
  const { setField, getField } = usePurchaseOrderForm()
  const orderLines = getField("orderLines") as OrderLine[]

  // Safely get values with null checks
  const productId = row.original.productId
  const priceUomId = row.original.priceUomId
  const currentPrice = row.original.pricePerUom ?? 0
  const product = row.original.product
  const isManualPriceSet = !!row.original.isManualPriceSet

  const { calculatedPricing, fetching: pricingPackagesFetching } = useCalculatedPricing()

  const productCalculatedPricing = (calculatedPricing?.orderLines || []).find((p) => p.productId === productId)

  const pricingPackage = productCalculatedPricing?.purchasingCost as PurchaseOrderPurchasingCost | undefined

  useEffect(() => {
    if (!product) {
      return
    }

    const updatedOrderLines = getUpdatedLines({
      rowIndex: row.index,
      orderLines,
      pricingPackage,
      tierPrice: productCalculatedPricing?.activePurchasingCostTier?.price ?? 0,
    })

    if (!updatedOrderLines) {
      return
    }

    setField("orderLines", updatedOrderLines, { shouldDirty: true })
  }, [
    pricingPackage,
    setField,
    orderLines,
    product,
    row.index,
    productCalculatedPricing?.activePurchasingCostTier?.price,
  ])

  const pricingUomId = isManualPriceSet
    ? priceUomId
    : (productCalculatedPricing?.activePurchasingCostTier?.uomId as string)

  const price: UomAmountWithId | null =
    currentPrice || isManualPriceSet
      ? {
          amount: currentPrice,
          uomId: pricingUomId,
        }
      : null

  const convertedPrice: UomAmountWithId | null =
    productCalculatedPricing?.converted?.pricePerUnit && productCalculatedPricing?.converted?.pricePerUnitUomId
      ? {
          amount: productCalculatedPricing.converted.pricePerUnit,
          uomId: productCalculatedPricing.converted.pricePerUnitUomId,
        }
      : null

  return {
    pricingPackage,
    price,
    convertedPrice,
    total: productCalculatedPricing?.lineTotalPrice || 0,
    fetching: pricingPackagesFetching,
    isManualPriceSet,
  }
}
